import { Injectable, signal, computed, WritableSignal, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';

export interface DashboardFilterState {
  minDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  granularity?: 'day' | 'week' | 'month';
}

@Injectable({
  providedIn: 'root'
})
export class FilterDashboardService {


  private readonly _minDate: WritableSignal<Date | null> = signal<Date | null>(new Date(2026, 0, 1, 0, 0, 0));


  public readonly minDate: Signal<Date | null> = this._minDate.asReadonly();

  public readonly minDate$: Observable<Date | null> = toObservable(this._minDate);


  private readonly _startDate: WritableSignal<Date | null > = signal<Date | null>(dayjs().startOf('month').toDate());
  public readonly startDate: Signal<Date | null> = this._startDate.asReadonly();
  public readonly startDate$: Observable<Date | null> = toObservable(this._startDate);

  /**
   * Signal interno opcional para la fecha final seleccionada.
   */
  private readonly _endDate: WritableSignal<Date | null> = signal<Date | null>(null);
  public readonly endDate: Signal<Date | null> = this._endDate.asReadonly();
  public readonly endDate$: Observable<Date | null> = toObservable(this._endDate);

  /**
   * Computed signal que resume el estado completo de los filtros de fecha.
   */
  public readonly filterState: Signal<DashboardFilterState> = computed(() => ({
    minDate: this._minDate(),
    startDate: this._startDate(),
    endDate: this._endDate(),
  }));

  /**
   * Obtiene el valor actual de la fecha mínima de búsqueda.
   */
  public getMinDate(): Date | null {
    return this._minDate();
  }

  /**
   * Establece o actualiza la fecha mínima de búsqueda.
   * Acepta un objeto `Date`, una cadena de fecha (`string`) o `null`/`undefined`.
   *
   * @param date Fecha mínima a establecer.
   */
  public setMinDate(date: Date | string | null | undefined): void {
    if (!date) {
      this._minDate.set(null);
      return;
    }

    if (date instanceof Date) {
      this._minDate.set(isNaN(date.getTime()) ? null : date);
    } else {
      const parsed = dayjs(date);
      this._minDate.set(parsed.isValid() ? parsed.toDate() : null);
    }
  }

  /**
   * Limpia / restablece la fecha mínima de búsqueda a null.
   */
  public clearMinDate(): void {
    this._minDate.set(null);
  }

  /**
   * Establece el rango de fechas seleccionado (startDate y endDate).
   */
  public setSelectedDates(startDate: Date | null, endDate: Date | null): void {
    this._startDate.set(startDate);
    this._endDate.set(endDate);
  }

  /**
   * Restablece todos los valores a su estado inicial.
   */
  public reset(): void {
    this._minDate.set(null);
    this._startDate.set(null);
    this._endDate.set(null);
  }
}
